import React, { useState, useEffect, useMemo } from 'react'
import useAuth from '../../hooks/useAuth'
import Modal from '../../components/Modal'
import { useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import Contact from '../../components/Contact'
import StaffWellbeingPortalPromo from '../../components/StaffWellbeingPortalPromo'
import OHPackagePopup from './components/OHPackagePopup'
import OHPackageBanner from './components/OHPackageBanner'

const STAFF_PORTAL_CODE_STORAGE_PREFIX =
  'staff_wellbeing_portal_access_code_'

const OH_PLUS_BANNER_IMAGES = {
  low: '/images/Oh 0-99.png',
  medium: '/images/Oh 100-199.png',
  high: '/images/Oh 200+.png',
}

const OH_PLUS_CLICK_HERE_IMAGE = '/images/click-here.png'

const OH_PLUS_PRICING_TIERS = [
  { min: 0, max: 99, label: '0-99', standard: 21.0, discounted: 16.5 },
  { min: 100, max: 199, label: '100-199', standard: 19.0, discounted: 14.5 },
  {
    min: 200,
    max: Number.POSITIVE_INFINITY,
    label: '200+',
    standard: 16.0,
    discounted: 11.5,
  },
]

const OH_PLUS_MIN_PACKAGE_PRICE = 500

const createDefaultPolicyMetrics = () => ({
  sai: { staff_count: 0, policy_id: null },
  swb: { staff_count: 0, policy_id: null },
  source: null,
  selected_staff_count: 0,
  has_oh_plus: false,
})

const extractPolicyMetricsPayload = (metricsResponse) =>
  metricsResponse?.data?.result?.data || metricsResponse?.data?.result || null

const toNonNegativeInteger = (value) => {
  const parsedValue = Number.parseInt(String(value ?? '').trim(), 10)
  if (!Number.isFinite(parsedValue) || parsedValue < 0) return 0
  return parsedValue
}

const getFirstFiniteRoundedValue = (values, { positiveOnly = false } = {}) => {
  for (const value of values) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) continue
    if (positiveOnly && numericValue <= 0) continue
    if (!positiveOnly && numericValue < 0) continue
    return Math.round(numericValue)
  }

  return null
}

const getOhPlusTierByHeadcount = (headcount) => {
  const count = Number.isFinite(headcount) ? headcount : 0
  return (
    OH_PLUS_PRICING_TIERS.find((tier) => count >= tier.min && count <= tier.max) ||
    OH_PLUS_PRICING_TIERS[0]
  )
}

const formatGBP = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

const hasOhPlusProduct = (policy) => {
  const product = policy?.product
  return typeof product === 'string' && product.toUpperCase().includes('OH+')
}

const getOhPlusBannerImageByVolume = (volume) => {
  if (volume >= 200) return OH_PLUS_BANNER_IMAGES.high
  if (volume >= 100) return OH_PLUS_BANNER_IMAGES.medium
  return OH_PLUS_BANNER_IMAGES.low
}

const getStaffPortalAccessCodeFromObject = (obj) => {
  if (!obj || typeof obj !== 'object') return ''

  const candidateKeys = [
    'staff_portal_access_code',
    'staffPortalAccessCode',
    'staff_portal_code',
    'staffPortalCode',
    'portal_access_code',
    'portalAccessCode',
    'portal_code',
    'portalCode',
    'swb_portal_code',
    'swbPortalCode',
    'wellbeing_portal_code',
    'wellbeingPortalCode',
    'access_code',
    'accessCode',
    'code',
  ]

  for (const key of candidateKeys) {
    const value = obj?.[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && String(value).trim()) return String(value)
  }

  return ''
}

const safeLocalStorageGet = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    // ignore
  }
}

const Dashboard = () => {
  const { auth, setAuth, setShowLoader, mainDpt, setMainDpt } = useAuth()
  const axios = useAxiosPrivate()

  const [currentOrg, setCurrentOrg] = useState({})
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState('')
  const [saiPolicy, setSaiPolicy] = useState([])
  const [srmPolicy, setSrmPolicy] = useState([])
  const [swbPolicy, setSWBPolicy] = useState([])
  const [ohPolicy, setOHPolicy] = useState([])
  const [urnData, setUrnData] = useState()
  const [policyMetrics, setPolicyMetrics] = useState(createDefaultPolicyMetrics)
  const [loadingPolicyMetrics, setLoadingPolicyMetrics] = useState(false)
  const [loadedPolicies, setLoadedPolicies] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [modalTitle, setModalTitle] = useState({})
  const [showOHPackagePopup, setShowOHPackagePopup] = useState(false)
  const [ohPackageHeadcountInput, setOhPackageHeadcountInput] = useState('')
  const [ohEnquirySubmitting, setOhEnquirySubmitting] = useState(false)
  const [ohEnquiryStatus, setOhEnquiryStatus] = useState('')
  const navigate = useNavigate()

  const handleClick = (dpt, contact, policy = null) => {
    let url
    switch (dpt) {
      case 'SWB':
        if (contact.wbo !== 1 || policy.product === 'SWB OH+') {
          return
        }
        url = `${process.env.REACT_APP_WBO}/dashboard?token=${token}`
        window.location.href = url
        break
      case 'OH':
        if (contact.oh !== 1) {
          return
        }

        setAuth({ ...auth, currentPolicy: policy })
        localStorage.setItem('currentPolicy', JSON.stringify(policy))
        navigate('/oh/dashboard')

        break

      case 'SAI':
        if (contact.sai !== 1 || !policy?.department) {
          return
        }
        url = `${process.env.REACT_APP_SAS}/authenticate-token?token=${token}`
        if (policy?.department === 'Mutual') {
          url = `${process.env.REACT_APP_MUTUAL}/authenticate-token?token=${token}`
        }
        if (policy?.department === 'SRM') {
          url = `${process.env.REACT_APP_SRM}/authenticate-token?token=${token}`
        }
        if (policy?.department === 'CC') {
          url = `${process.env.REACT_APP_CC}/authenticate-token?token=${token}`
        }
        window.location.href = url
        break
      default:
        break
    }
    // window.location.href = url
  }

  const openLogoutModal = () => {
    setModalTitle(`Your details could not be verified`)
    setShowModal(true)
  }

  const ModalBody = () => {
    if (showModal) {
      return <NoPolicyFoundElement />
    }

    return null
  }

  const NoPolicyFoundElement = () => {
    const redirectToLogin = async (e) => {
      window.location.reload()
    }

    return (
      <form>
        <div className="mb-3 py-4 text-center">
          We could not verify your details. <br /> Please contact your account
          manager.
        </div>
        <div className="flex mt-2 w-full">
          <button
            type="button"
            className="btn red mt-4 w-full"
            onClick={redirectToLogin}
          >
            Close
          </button>
        </div>
      </form>
    )
  }

  const resetModal = (resetNotes = true) => {
    setShowModal(false)
  }

  const PolicyStatus = ({ status }) => {
    let bg = ''
    let text = ''
    let title = ''
    let dot = ''
    switch (status) {
      case 'live':
        bg = 'bg-green-200'
        dot = 'bg-green-600'
        text = 'text-green-600'
        title = 'Active'
        break
      case 'previous':
        bg = 'bg-rose-300/30'
        dot = 'bg-rose-600/50'
        text = 'text-rose-600/50'
        title = 'Expired'
        break
      case 'expiring':
        bg = 'bg-amber-300/30'
        text = 'text-amber-600/60'
        dot = 'bg-amber-600/60'
        title = 'Expiring'
        break
      case 'accepted':
        bg = 'bg-amber-300/30'
        text = 'text-amber-600/60'
        dot = 'bg-amber-600/60'
        title = 'Pending'
        break
      default:
    }

    return (
      <div
        className={`rounded ${bg} w-1/2 p-2.5 flex justify-center items-center ${text} text-base`}
      >
        <span className={`w-2 h-2 block rounded-full ${dot} mr-3`}>&nbsp;</span>
        <span className="font-bold">{title}</span>
      </div>
    )
  }

  const Policy = ({ dpt, policy }) => {
    let text = ''
    switch (dpt) {
      case 'sai':
        text = `Manage your staff details, holidays ${policy.department !== 'Mutual' && policy.department !== 'GBS'
          ? 'and absence claims'
          : 'and payment requests'
          }.`
        break
      case 'swb':
        text = 'Click here to manage your Standalone Wellbeing Agreement'
        break
      case 'oh':
        text =
          'Click here to create and manage your Occupational Health Requests'
        break
      default:
        break
    }
    return (
      <>
        <p className="text-center text-sm lg:text-sm text-[#201E5F]/80 mt-4 lg:min-h-[80px]">
          {text}
        </p>
        <div className="flex justify-between mt-8 space-x-4">
          <div className="rounded bg-purple-300/30 w-1/2 p-2.5 text-sm flex justify-center items-center text-purple-800/50 xl:text-base font-bold">
            {policy?.policy_reference}
          </div>
          <PolicyStatus status={policy.policy_status} />
        </div>
      </>
    )
  }

  const NotAllowed = ({ dpt }) => {
    return (
      <>
        <p className="text-center text-base text-[#201E5F]/80 mt-4 lg:min-h-[100px]">
          You currently don't have access to this product. Please contact the
          main contact at your school/trust if you believe this is an error.
        </p>
        <div className="flex justify-between mt-8">
          <div className="rounded bg-rose-300/60 w-full text-sm p-2.5 flex justify-center items-center text-rose-600/60 xl:text-base font-bold cursor-not-allowed">
            You have no access to this product
          </div>
        </div>
      </>
    )
  }

  const PolicyOh = () => {
    return (
      <>
        <p className="text-center text-sm lg:text-sm text-[#201E5F]/80 mt-4 lg:min-h-[80px]">
          Click here to create and manage your Occupational Health Requests
        </p>
        <div className="flex justify-center mt-8">
          <div
            className={`rounded bg-green-200 w-full p-2.5 flex justify-center items-center text-green-600 text-base`}
          >
            <span className="font-bold">Active</span>
          </div>
        </div>
      </>
    )
  }

  const NoPolicy = ({ dpt }) => {
    let text = ''
    switch (dpt) {
      case 'sai':
        text = "You currently don't have a staff absence cover."
        break
      case 'swb':
        text =
          'You do not have a subscription to one of our separate, standalone Wellbeing Schemes.'
        break
      case 'oh':
        text =
          'You do not have a subscription for our extended Occupational Health services.'
        break
      default:
        break
    }
    return (
      <>
        <p className="text-center text-base text-[#201E5F]/80 mt-4 lg:min-h-[100px]">
          {text}
        </p>
        <div className="flex justify-between">
          <p className="w-full text-sm p-2.5 text-center xl:text-base">
            Please contact your Account Manager to discuss any additional{' '}
            {dpt === 'oh' && 'Occupational Health'}
            {dpt === 'sai' && 'Staff Absence'}
            {dpt === 'swb' && 'Wellbeing'} requirements.
          </p>
        </div>
      </>
    )
  }

  const ShowSAIButton = ({ type, policy }) => {
    switch (type) {
      case 'policy':
        return <Policy dpt="sai" policy={policy} />
      case 'not-allowed':
        return <NotAllowed dpt="sai" />
      case 'no-policy':
        return <NoPolicy dpt="sai" />
      default:
        break
    }
  }
  const ShowSWBButton = ({ type, policy }) => {
    switch (type) {
      case 'policy':
        return <Policy dpt="swb" policy={policy} />
      case 'not-allowed':
        return <NotAllowed dpt="swb" />
      case 'no-policy':
        return <NoPolicy dpt="swb" />
      default:
        break
    }
  }
  const ShowOHButton = ({ type, policy }) => {
    if (!auth?.user?.oh) {
      return <NotAllowed dpt="oh" />
    } else if (ohPolicy) {
      return <PolicyOh policy={ohPolicy} />
    } else if (
      saiPolicy &&
      (saiPolicy.department === 'Mutual' ||
        saiPolicy.department === 'SAS' ||
        saiPolicy.department === 'SRM')
    ) {
      return <PolicyOh policy={saiPolicy} />
    } else {
      return <NoPolicy dpt="oh" />
    }
  }

  const fetchPolicyMetrics = async (policies) => {
    if (!policies) return

    try {
      setLoadingPolicyMetrics(true)
      const metricsResponse = await axios.post('/staff-hub/policy-metrics', {
        policies,
      })

      setPolicyMetrics(extractPolicyMetricsPayload(metricsResponse) || createDefaultPolicyMetrics())
    } catch (error) {
      setPolicyMetrics(createDefaultPolicyMetrics())
    } finally {
      setLoadingPolicyMetrics(false)
    }
  }

  const getPolicys = async () => {
    setShowLoader(true)
    const response = await axios.post('/staff-hub/policy', {
      token: auth.token,
      orgId: auth.schools[0].id ?? auth?.schools[0].id,
      user: auth?.user?.contact_id,
    })

    if (response.data.error === true) {
      setShowLoader(false)
      setLoading(false)
      openLogoutModal()
    } else {
      const data = response.data.result?.data || {}
      const fetchedPolicies = data?.policies || {}
      const org = data?.org || {}

      setUrnData(org?.academy_urn || org?.trust_urn || '')

      setSaiPolicy(fetchedPolicies?.sai)
      setCurrentOrg(org)
      if (fetchedPolicies?.sai) {
        setMainDpt(fetchedPolicies?.sai?.department)
      } else {
        setMainDpt('SAS')
      }
      setSrmPolicy(fetchedPolicies?.srm)
      if (fetchedPolicies?.srm) {
        setMainDpt(fetchedPolicies?.srm?.department)
      }

      setSWBPolicy(fetchedPolicies?.swb)
      setOHPolicy(fetchedPolicies?.oh)
      setToken(localStorage.getItem('token'))
      setLoading(false)

      const orgId = auth?.schools?.[0]?.id
      if (orgId) {
        await fetchPolicyMetrics(fetchedPolicies)

        const orgCode = getStaffPortalAccessCodeFromObject(org)
        const schoolCode = getStaffPortalAccessCodeFromObject(
          auth?.schools?.[0]
        )
        const swbPolicyCode = getStaffPortalAccessCodeFromObject(
          fetchedPolicies?.swb
        )
        const ohPolicyCode = getStaffPortalAccessCodeFromObject(
          fetchedPolicies?.oh
        )
        const saiPolicyCode = getStaffPortalAccessCodeFromObject(
          fetchedPolicies?.sai
        )

        const accessCode =
          orgCode || schoolCode || swbPolicyCode || ohPolicyCode || saiPolicyCode
        if (accessCode) {
          safeLocalStorageSet(
            `${STAFF_PORTAL_CODE_STORAGE_PREFIX}${orgId}`,
            accessCode
          )
        }
      }

      localStorage.setItem(
        'orgType',
        JSON.stringify(
          org?.trust_name
            ? {
              type: 'trust',
              postcode: org?.trust_postcode,
              name: org?.trust_name,
            }
            : {
              type: 'academy',
              postcode: org?.academy_postcode,
              name: org?.academy_name,
            }
        )
      )
    }

    setShowLoader(false)
  }

  useEffect(() => {
    if (!loadedPolicies) {
      setLoadedPolicies(true)
      getPolicys()
      localStorage.removeItem('currentPolicy')
    }
  }, [auth])

  useEffect(() => {
    if (mainDpt === 'CC') {
      handleClick('SAI', auth.user, saiPolicy)
    }
  }, [mainDpt])

  const staffWellbeingPortalAccessCode = useMemo(() => {
    const school = auth?.schools?.[0] || {}
    const orgId = school?.id
    const cachedCode = orgId
      ? safeLocalStorageGet(`${STAFF_PORTAL_CODE_STORAGE_PREFIX}${orgId}`)
      : null

    return (
      getStaffPortalAccessCodeFromObject(school) ||
      getStaffPortalAccessCodeFromObject(currentOrg) ||
      getStaffPortalAccessCodeFromObject(swbPolicy) ||
      getStaffPortalAccessCodeFromObject(ohPolicy) ||
      getStaffPortalAccessCodeFromObject(saiPolicy) ||
      cachedCode ||
      ''
    )
  }, [auth?.schools, currentOrg, swbPolicy, ohPolicy, saiPolicy])

  const staffWellbeingPortalPopupStorageKey = useMemo(() => {
    const orgId = auth?.schools?.[0]?.id
    if (!orgId) return null
    return `staff_wellbeing_portal_launch_popup_dismissed_v1_${orgId}`
  }, [auth?.schools])

  const hasAnyOhPlusPolicy = useMemo(() => {
    if (typeof policyMetrics?.has_oh_plus === 'boolean') {
      return policyMetrics.has_oh_plus
    }

    return [saiPolicy, swbPolicy, ohPolicy].some((policy) =>
      hasOhPlusProduct(policy)
    )
  }, [policyMetrics, saiPolicy, swbPolicy, ohPolicy])

  const bannerMetricType = useMemo(() => {
    if (policyMetrics?.source === 'sai' || policyMetrics?.source === 'swb') {
      return policyMetrics.source
    }

    if (saiPolicy) return 'sai'
    if (swbPolicy) return 'swb'
    return null
  }, [policyMetrics, saiPolicy, swbPolicy])

  const bannerVolume = useMemo(() => {
    if (policyMetrics?.source && Number(policyMetrics?.selected_staff_count) > 0) {
      return Number(policyMetrics.selected_staff_count)
    }

    if (bannerMetricType === 'sai') {
      return Number(policyMetrics?.sai?.staff_count || 0)
    }

    if (bannerMetricType === 'swb') {
      return Number(policyMetrics?.swb?.staff_count || 0)
    }

    return 0
  }, [bannerMetricType, policyMetrics])

  const shouldShowOhPlusBanner =
    !loading &&
    !loadingPolicyMetrics &&
    !hasAnyOhPlusPolicy &&
    !!bannerMetricType

  const shouldReserveBannerSpace =
    shouldShowOhPlusBanner || loading || loadingPolicyMetrics

  const ohPlusBannerImage = useMemo(
    () => getOhPlusBannerImageByVolume(bannerVolume),
    [bannerVolume]
  )

  const ohHeadcountPrefill = useMemo(() => {
    const candidates = [
      policyMetrics?.selected_staff_count,
      policyMetrics?.sai?.staff_count,
      policyMetrics?.swb?.staff_count,
      bannerVolume,
    ]

    return (
      getFirstFiniteRoundedValue(candidates, { positiveOnly: true }) ??
      getFirstFiniteRoundedValue(candidates) ??
      0
    )
  }, [policyMetrics, bannerVolume])

  const parsedOHPackageHeadcount = useMemo(() => {
    return toNonNegativeInteger(ohPackageHeadcountInput)
  }, [ohPackageHeadcountInput])

  const ohPricing = useMemo(() => {
    const tier = getOhPlusTierByHeadcount(parsedOHPackageHeadcount)
    const standardTotalRaw = parsedOHPackageHeadcount * tier.standard
    const discountedTotalRaw = parsedOHPackageHeadcount * tier.discounted
    const standardTotal = Math.max(OH_PLUS_MIN_PACKAGE_PRICE, standardTotalRaw)
    const discountedTotal = Math.max(OH_PLUS_MIN_PACKAGE_PRICE, discountedTotalRaw)

    return {
      tier,
      standardTotal,
      discountedTotal,
      isMinimumPriceApplied: discountedTotalRaw < OH_PLUS_MIN_PACKAGE_PRICE,
      savingsTotal: standardTotal - discountedTotal,
    }
  }, [parsedOHPackageHeadcount])

  const openOHPackagePopup = () => {
    setOhPackageHeadcountInput(String(ohHeadcountPrefill))

    setOhEnquiryStatus('')
    setShowOHPackagePopup(true)
  }

  const submitOHEnquiry = async () => {
    if (ohEnquirySubmitting) return

    try {
      setOhEnquirySubmitting(true)
      setOhEnquiryStatus('')

      await axios.post('/send-email', {
        type: 'oh-plus-enquiry',
        payload: {
          user_name: auth?.user?.contact_name || '',
          user_email: auth?.user?.contact_email || '',
          school_name: auth?.schools?.[0]?.name || '',
          school_urn: urnData || '',
          headcount: parsedOHPackageHeadcount,
          pricing_band: ohPricing.tier.label,
          standard_rate: formatGBP(ohPricing.tier.standard),
          discounted_rate: formatGBP(ohPricing.tier.discounted),
          minimum_price: formatGBP(OH_PLUS_MIN_PACKAGE_PRICE),
          quoted_price: formatGBP(ohPricing.discountedTotal),
          saving: formatGBP(ohPricing.savingsTotal),
        },
      })

      setOhEnquiryStatus('Enquiry sent to sales successfully.')
      setTimeout(() => {
        setShowOHPackagePopup(false)
        setOhEnquiryStatus('')
      }, 3000)
    } catch (error) {
      setOhEnquiryStatus('Unable to send enquiry right now. Please try again.')
    } finally {
      setOhEnquirySubmitting(false)
    }
  }

  return (
    <>
      <OHPackagePopup
        open={showOHPackagePopup}
        onClose={() => setShowOHPackagePopup(false)}
        headcountInput={ohPackageHeadcountInput}
        onHeadcountChange={setOhPackageHeadcountInput}
        parsedHeadcount={parsedOHPackageHeadcount}
        pricing={ohPricing}
        enquirySubmitting={ohEnquirySubmitting}
        enquiryStatus={ohEnquiryStatus}
        onSubmit={submitOHEnquiry}
        formatGBP={formatGBP}
        minPackagePrice={OH_PLUS_MIN_PACKAGE_PRICE}
      />

      {showModal && (
        <Modal title={modalTitle} body={<ModalBody />} show={resetModal} />
      )}
      <div className="w-full max-w-[1700px] mx-auto flex flex-col lg:flex-row lg:items-start lg:space-x-8">
        <OHPackageBanner
          showContainer={shouldReserveBannerSpace}
          showBanner={shouldShowOhPlusBanner}
          bannerImage={ohPlusBannerImage}
          clickHereImage={OH_PLUS_CLICK_HERE_IMAGE}
          onOpenPopup={openOHPackagePopup}
        />

        <div className="w-full relative z-0">
      <div className="mb-8 sm:mb-16">
        <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white text-center pt-12 ">
          Welcome Back, {auth?.user?.contact_name}
        </h3>
        <h4 className="text-lg font-medium text-white text-center">
          You are now viewing {auth?.schools[0]?.name}, which product would you
          like to view?
        </h4>
      </div>

      <div className="w-full max-w-7xl">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-5">
          {!srmPolicy && (
            <div
              className={`w-full bg-white shadow-lg rounded-md overflow-hidden flex flex-col-reverse lg:flex-row-reverse ${auth?.user?.sai === 1 && saiPolicy
                ? ' cursor-pointer'
                : ' cursor-not-allowed'
                }`}
              onClick={() => {
                saiPolicy && handleClick('SAI', auth.user, saiPolicy)
              }}
            >
              <div className="w-full p-6 lg:p-4 xl:p-5 min-h-[210px] xl:min-h-[230px]">
                <h3 className="text-center text-lg sm:text-xl lg:text-base xl:text-lg text-[#201E5F] font-semibold">
                  {loading ? (
                    'Loading...'
                  ) : (
                    <>
                      {saiPolicy?.department === 'GBS' ||
                        saiPolicy?.department === 'Mutual'
                        ? 'Staff Absence Agreement'
                        : 'Staff Absence Policy'}
                    </>
                  )}
                </h3>
                {loading ? null : (
                  <ShowSAIButton
                    type={
                      auth?.user?.sai
                        ? saiPolicy
                          ? 'policy'
                          : 'no-policy'
                        : 'not-allowed'
                    }
                    policy={saiPolicy}
                  />
                )}
              </div>
            </div>
          )}
          <div
            className={`w-full bg-white shadow-lg rounded-md overflow-hidden flex flex-col-reverse lg:flex-row-reverse ${auth?.user?.wbo === 1 &&
              swbPolicy &&
              swbPolicy?.product !== 'SWB OH+'
              ? ' cursor-pointer'
              : ' cursor-not-allowed'
              }`}
            onClick={() => {
              swbPolicy && handleClick('SWB', auth.user, swbPolicy)
            }}
          >
            <div className="w-full p-6 lg:p-4 xl:p-5 min-h-[210px] xl:min-h-[230px]">
              <h3 className="text-center text-lg sm:text-xl lg:text-base xl:text-lg text-[#201E5F] font-semibold">
                {loading
                  ? 'Loading...'
                  : mainDpt === 'CC'
                    ? 'Coming Soon...'
                    : 'Whole School Wellbeing'}
              </h3>
              {loading ? null : (
                <ShowSWBButton
                  type={
                    auth?.user?.wbo
                      ? swbPolicy?.product === 'SWB OH+'
                        ? 'not-allowed'
                        : swbPolicy
                          ? 'policy'
                          : 'no-policy'
                      : 'not-allowed'
                  }
                  policy={swbPolicy}
                />
              )}
            </div>
          </div>

          <div
            className={`w-full bg-white shadow-lg rounded-md overflow-hidden flex flex-col-reverse lg:flex-row-reverse ${auth?.user?.oh === 1 && (ohPolicy || saiPolicy)
              ? ' cursor-pointer'
              : ' cursor-not-allowed'
              }`}
            onClick={() => {
              ohPolicy
                ? handleClick('OH', auth.user, ohPolicy)
                : saiPolicy && handleClick('OH', auth.user, saiPolicy)
            }}
          >
            <div
              className={`w-full p-6 lg:p-4 min-h-[210px] xl:min-h-[230px] xl:p-5 ${auth?.user?.oh === 1 && (ohPolicy || saiPolicy)
                ? ' cursor-pointer'
                : ' cursor-not-allowed'
                }`}
            >
              <h3 className="text-center text-xl lg:text-base xl:text-lg text-[#201E5F] font-semibold">
                {loading
                  ? 'Loading...'
                  : mainDpt === 'CC'
                    ? 'Coming Soon...'
                    : 'Occupational Health'}
              </h3>
              {loading ? null : (
                <ShowOHButton
                  type={
                    auth?.user?.oh
                      ? ohPolicy
                        ? 'policy'
                        : 'no-policy'
                      : 'not-allowed'
                  }
                  policy={ohPolicy}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <StaffWellbeingPortalPromo
        accessCode={staffWellbeingPortalAccessCode}
        storageKey={staffWellbeingPortalPopupStorageKey}
      />

      <Contact />
        </div>
      </div>
    </>
  )
}

export default Dashboard
